import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CoreDropdown } from '@komodo-design-system/komodo-components-react';
import { fetchNifiReport, fetchNifiDailyTrends, setNifiReportFilters } from 'actions/nifiReport';
import { getDateRange, DATE_RANGE_OPTIONS } from 'utils/dateUtils';
import SourcePieChart from './SourcePieChart';
import StatusBarChart from './StatusBarChart';
import DataTable from './DataTable';
import DailyTrendsChart from './DailyTrendsChart';
import './NifiReport.scss';

const APPS_OPTIONS = [
  { label: 'Nifi', value: 'Nifi' }
];

// Convert DATE_RANGE_OPTIONS to Komodo dropdown format
const DATE_RANGE_DROPDOWN_OPTIONS = DATE_RANGE_OPTIONS.map(option => ({
  label: option.label,
  value: option.value
}));

const NifiReport: React.FC = () => {
  const dispatch: any = useDispatch();
  const { 
    data, 
    loading, 
    error, 
    filters,
    trendsData,
    trendsLoading,
    trendsError 
  } = useSelector((state: any) => state.nifiReportReducer);
  
  const [selectedDateRange, setSelectedDateRange] = useState('last-7-days');
  const [sourceFilter, setSourceFilter] = useState('');
  const [appsFilter, setAppsFilter] = useState('Nifi');
  const [activeTab, setActiveTab] = useState('nifi');

  useEffect(() => {
    // Load initial data with last 7 days
    const { startDate, endDate } = getDateRange('last-7-days');
    const initialFilters = {
      apps: appsFilter,
      source: '',
      startDate,
      endDate
    };
    dispatch(setNifiReportFilters(initialFilters));
    dispatch(fetchNifiReport(initialFilters));
    dispatch(fetchNifiDailyTrends(initialFilters));
  }, [dispatch, appsFilter]);

  const handleDateRangeChange = (range: string) => {
    setSelectedDateRange(range);
    const { startDate, endDate } = getDateRange(range);
    const newFilters = {
      ...filters,
      apps: appsFilter,
      startDate,
      endDate
    };
    dispatch(setNifiReportFilters(newFilters));
    dispatch(fetchNifiReport(newFilters));
    dispatch(fetchNifiDailyTrends(newFilters));
  };

  const handleSourceFilterChange = (source: string) => {
    setSourceFilter(source);
    const newFilters = {
      ...filters,
      apps: appsFilter,
      source
    };
    dispatch(setNifiReportFilters(newFilters));
    dispatch(fetchNifiReport(newFilters));
    dispatch(fetchNifiDailyTrends(newFilters));
  };

  const handleAppsFilterChange = (apps: string) => {
    setAppsFilter(apps);
    const newFilters = {
      ...filters,
      apps,
      source: sourceFilter
    };
    dispatch(setNifiReportFilters(newFilters));
    dispatch(fetchNifiReport(newFilters));
    dispatch(fetchNifiDailyTrends(newFilters));
  };

  const getUniqueSources = () => {
    if (!data?.results) {
      return [];
    }
    const sourcesSet = new Set(data.results.map((item: any) => item.source));
    return Array.from(sourcesSet);
  };

  if (loading) {
    return (
      <div className="nifi-report-loading">
        <div className="spinner">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="nifi-report-error">
        <div className="error-message">
          <h3>Error loading Nifi Report</h3>
          <p>{error}</p>
          <button onClick={() => dispatch(fetchNifiReport(filters))}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="nifi-report-container">
      <div className="nifi-report-header">
        <h1>Digital Asset Management Report</h1>
        
        {/* Tab Navigation */}
        <div className="tab-navigation">
          <button 
            className={`tab-button ${activeTab === 'nifi' ? 'active' : ''}`}
            onClick={() => setActiveTab('nifi')}
          >
            Nifi Report
          </button>
          <button 
            className={`tab-button ${activeTab === 'dam' ? 'active' : ''}`}
            onClick={() => setActiveTab('dam')}
          >
            DAM Report
          </button>
        </div>
      </div>

      {/* Nifi Report Tab Content */}
      {activeTab === 'nifi' && (
        <div className="tab-content">
          <div className="filters-container">
            <div className="filter-group">
              <label htmlFor="appsFilter">Apps:</label>
              <CoreDropdown
                filterable
                wrap
                placeholder="Select Apps"
                functionality="single-select"
                className="filter-dropdown"
                size="regular"
                customkey="value"
                options={APPS_OPTIONS}
                selected={[appsFilter]}
                onSelectionChanged={(event) => {
                  const selectedOption = event.detail.option;
                  if (selectedOption?.value) {
                    handleAppsFilterChange(selectedOption.value);
                  }
                }}
                menusize={4}
              />
            </div>

            <div className="filter-group">
              <label htmlFor="dateRange">Date Range:</label>
              <CoreDropdown
                filterable
                wrap
                placeholder="Select Date Range"
                functionality="single-select"
                className="filter-dropdown"
                size="regular"
                customkey="value"
                options={DATE_RANGE_DROPDOWN_OPTIONS}
                selected={[selectedDateRange]}
                onSelectionChanged={(event) => {
                  const selectedOption = event.detail.option;
                  if (selectedOption?.value) {
                    handleDateRangeChange(selectedOption.value);
                  }
                }}
                menusize={4}
              />
            </div>

            <div className="filter-group">
              <label htmlFor="sourceFilter">Source Filter:</label>
              <CoreDropdown
                filterable
                wrap
                placeholder="All Sources"
                functionality="single-select"
                className="filter-dropdown"
                size="regular"
                customkey="value"
                options={[
                  { label: 'All Sources', value: '' },
                  ...getUniqueSources().map((source: unknown) => ({
                    label: source as string,
                    value: source as string
                  }))
                ]}
                selected={[sourceFilter]}
                onSelectionChanged={(event) => {
                  const selectedOption = event.detail.option;
                  if (selectedOption?.value !== undefined) {
                    handleSourceFilterChange(selectedOption.value);
                  }
                }}
                menusize={4}
              />
            </div>
          </div>

          {data?.query_parameters && (
            <div className="applied-filters">
              <h4>Applied Filters:</h4>
              <div className="filter-tags">
                <span className="filter-tag">Apps: {data.query_parameters.apps}</span>
                {data.query_parameters.source && (
                  <span className="filter-tag">Source: {data.query_parameters.source}</span>
                )}
                {data.query_parameters.start_date && (
                  <span className="filter-tag">
                    Date: {data.query_parameters.start_date} to {data.query_parameters.end_date}
                  </span>
                )}
              </div>
            </div>
          )}

          {loading ? (
            <div className="nifi-report-loading">
              <div className="spinner">Loading...</div>
            </div>
          ) : error ? (
            <div className="nifi-report-error">
              <div className="error-message">
                <h3>Error loading Nifi Report</h3>
                <p>{error}</p>
                <button onClick={() => dispatch(fetchNifiReport(filters))}>
                  Retry
                </button>
              </div>
            </div>
          ) : data?.results && data.results.length > 0 ? (
            <div className="charts-container">
              {/* Row 1: Pie Chart and Bar Chart */}
              <div className="chart-section half-width">
                <SourcePieChart data={data.results} />
              </div>
              
              <div className="chart-section half-width">
                <StatusBarChart data={data.results} />
              </div>

              {/* Row 2: Daily Trends (Full Width) */}
              <div className="chart-section full-width">
                {trendsLoading ? (
                  <div className="trends-loading">
                    <div className="spinner">Loading trends...</div>
                  </div>
                ) : trendsError ? (
                  <div className="trends-error">
                    <h3>Daily Trends</h3>
                    <p className="error-message">Error loading trends: {trendsError}</p>
                  </div>
                ) : (
                  <DailyTrendsChart data={trendsData || []} />
                )}
              </div>

              {/* Row 3: Summary Stats */}
              <div className="chart-section summary-tile">
                <h3>Summary</h3>
                <div className="summary-stats">
                  <div className="stat-card">
                    <h4>Total Groups</h4>
                    <p className="stat-value">{data.total_groups}</p>
                  </div>
                  <div className="stat-card">
                    <h4>Total Records</h4>
                    <p className="stat-value">
                      {data.results.reduce((total: number, item: any) => total + item.total_records, 0)}
                    </p>
                  </div>
                </div>
                
                {/* Status Summary */}
                <div className="status-summary">
                  <h4>Overall Status Distribution</h4>
                  <div className="status-grid">
                    {(() => {
                      const statusTotals = data.results.reduce((totals: any, item: any) => {
                        totals.published += item.status_counts.published || 0;
                        totals.unpublished += item.status_counts.unpublished || 0;
                        totals.in_progress += item.status_counts.in_progress || 0;
                        totals.invalid_file += item.status_counts.invalid_file || 0;
                        totals.failed += item.status_counts.failed || 0;
                        totals.filename_invalid += item.status_counts.filename_invalid || 0;
                        return totals;
                      }, {
                        published: 0,
                        unpublished: 0,
                        in_progress: 0,
                        invalid_file: 0,
                        failed: 0,
                        filename_invalid: 0
                      });

                      return Object.entries(statusTotals).map(([status, count]) => (
                        <div key={status} className="status-item">
                          <span className={`status-indicator ${status}`}></span>
                          <span className="status-label">{status.replace('_', ' ').toUpperCase()}</span>
                          <span className="status-count">{count as number}</span>
                        </div>
                      ));
                    })()}
                  </div>
                </div>
              </div>

              {/* Row 4: Data Table (Full Width) */}
              <div className="chart-section full-width">
                <DataTable data={data.results} />
              </div>
            </div>
          ) : (
            <div className="no-data">
              <h3>No data available</h3>
              <p>No records found for the selected filters.</p>
            </div>
          )}
        </div>
      )}

      {/* DAM Report Tab Content */}
      {activeTab === 'dam' && (
        <div className="tab-content">
          <div className="dam-report-placeholder">
            <h3>DAM Report</h3>
            <p>DAM Report functionality will be implemented here.</p>
            <div className="placeholder-content">
              <div className="placeholder-card">
                <h4>Digital Asset Management</h4>
                <p>This section will contain DAM-specific reports and analytics.</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NifiReport;
